import {
  createCart,
  retreiveCart,
  cleanCart,
  addOrUpdateCart,
} from '../api/cart';
import { deleteToken, getToken, setToken } from '../services/auth';
import { retreiveUser } from '../api/users';
import { setCart } from '../store/cart/cartSlice';
import { setUser } from '../store/user/userSlice';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';

export const useGetUserInfo = (setLogged: (action: boolean) => void) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const initializeUser = async () => {
      const justCheckedOut = localStorage.getItem('just_checked_out');
      if (justCheckedOut) {
        localStorage.removeItem('just_checked_out');
        if (!getToken().length) {
          setToken('guest-user');
          const newCart = await createCart();
          dispatch(setCart(newCart));
        } else {
          const user = await retreiveUser();
          dispatch(setUser(user));
          setLogged(true);
          const newCart = await createCart();
          dispatch(setCart(newCart));
        }
        return;
      }

      if (!getToken().length) {
        setToken('guest-user');
        initializeGuestCart();
      } else {
        getUserInfo();
      }
    };

    initializeUser();
  }, []);

  const initializeGuestCart = async () => {
    try {
      const guestCartId = localStorage.getItem('guest-user-cart');
      let cart;

      if (guestCartId) {
        try {
          cart = await retreiveCart({ id: guestCartId });
        } catch (error: any) {
          if (error.response?.status === 404) {
            cart = await createCart();
            localStorage.setItem('guest-user-cart', cart.reference);
          } else {
            throw error;
          }
        }
      } else {
        cart = await createCart();
        localStorage.setItem('guest-user-cart', cart.reference);
      }

      dispatch(setCart(cart));
    } catch (error: any) {
      console.error('Error initializing guest cart:', error);
    }
  };

  const getOrCreateCart = async (id: string | null) => {
    try {
      let cart;
      if (!id || !id.length) {
        cart = await createCart();
      } else {
        cart = await retreiveCart({ id });
      }
      dispatch(setCart(cart));
      return cart;
    } catch (error: any) {
      if (error.response?.status === 404) {
        const newCart = await createCart();
        dispatch(setCart(newCart));
        return newCart;
      }
      throw new Error(error);
    }
  };

  const transferGuestCartToUser = async (guestCartId: string) => {
    try {
      const guestCart = await retreiveCart({ id: guestCartId });

      if (guestCart.processed || guestCart.orderId) {
        localStorage.removeItem('guest-user-cart');
        return;
      }

      const user = await retreiveUser();
      const userCartId =
        user.currentUserCart?.reference || user.currentUserCart;

      if (!userCartId) {
        throw new Error('User does not have a current cart.');
      }

      await cleanCart({ id: userCartId });

      if (guestCart.branchStores) {
        for (const branchStore of guestCart.branchStores) {
          for (const product of branchStore.products || []) {
            await addOrUpdateCart({
              id: userCartId,
              params: {
                storeBranchProduct: product.storeBranchProduct,
                quantity: product.quantity,
                observation: product.observation,
                attributes: product.attributes || {},
              },
            });
          }
        }
      }

      const finalCart = await retreiveCart({ id: userCartId });
      dispatch(setCart(finalCart));

      localStorage.removeItem('guest-user-cart');
    } catch (error: any) {
      console.error('Error transferring guest cart to user:', error);
      throw new Error(error);
    }
  };

  const saveAuthenticatedCart = async () => {
    try {
      const userCart = await retreiveCart({ id: 'user-cart-id' });
      if (
        userCart &&
        userCart.productsCount > 0 &&
        !userCart.processed &&
        !userCart.orderId
      ) {
        localStorage.setItem('user-cart-backup', JSON.stringify(userCart));
      }
    } catch (error: any) {
      console.error('Error saving authenticated cart:', error);
      throw new Error(error);
    }
  };

  const logout = async () => {
    await saveAuthenticatedCart();
    deleteToken();
    setLogged(false);
  };

  const getUserInfo = async () => {
    try {
      if (getToken() === 'guest-user') {
        await initializeGuestCart();
        return;
      }

      const user = await retreiveUser();
      dispatch(setUser(user));
      setLogged(true);

      if (localStorage.getItem('just_checked_out')) {
        localStorage.removeItem('just_checked_out');
        const newCart = await createCart();
        dispatch(setCart(newCart));
        return;
      }

      const guestCartId = localStorage.getItem('guest-user-cart');

      if (guestCartId) {
        const guestCart = await retreiveCart({ id: guestCartId });

        if (
          !guestCart.processed &&
          !guestCart.orderId &&
          guestCart.productsCount > 0
        ) {
          await transferGuestCartToUser(guestCartId);
        } else {
          localStorage.removeItem('guest-user-cart');
          const userCartBackup = localStorage.getItem('user-cart-backup');
          if (userCartBackup) {
            const cart = JSON.parse(userCartBackup);
            if (cart.productsCount > 0 && !cart.processed && !cart.orderId) {
              try {
                const validCart = await retreiveCart({ id: cart.reference });
                if (validCart && !validCart.processed && !validCart.orderId) {
                  dispatch(setCart(validCart));
                  return;
                }
              } catch (error) {
                console.error('Error retrieving backup cart:', error);
              }
            }
            localStorage.removeItem('user-cart-backup');
          }
          await getOrCreateCart(user.currentUserCart);
        }
      } else {
        const userCartBackup = localStorage.getItem('user-cart-backup');
        if (userCartBackup) {
          const cart = JSON.parse(userCartBackup);
          if (cart.productsCount > 0 && !cart.processed && !cart.orderId) {
            try {
              const validCart = await retreiveCart({ id: cart.reference });
              if (validCart && !validCart.processed && !validCart.orderId) {
                dispatch(setCart(validCart));
                return;
              }
            } catch (error) {
              console.error('Error retrieving backup cart:', error);
            }
          }
          localStorage.removeItem('user-cart-backup');
        }
        await getOrCreateCart(user.currentUserCart);
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        await saveAuthenticatedCart();
        deleteToken();
      } else {
        console.error('Error getting user info:', error);
        throw new Error(error);
      }
    }
  };

  return { logout };
};
